import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Select from "react-select";
import { FaTimes } from "react-icons/fa";
import { selectStyles } from "../../../../pages/AdminPanelPage/components/ManageUserTab/types/SelectStyles";
import { FileDropzone } from "../AddRegulatorModal/components/FileDropzone";
import { useMutation } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { addFeedback } from "./services/addFeedback";
import { useLocation, useParams } from "react-router-dom";

interface FeedbackModalProps {
  setShowModal: (v: boolean) => void;
  darkMode: boolean;
  regulatorCodeParam: string;
  idParam: string;
}

const FeedbackModal: React.FC<FeedbackModalProps> = ({
  setShowModal,
  darkMode,
  regulatorCodeParam,
  idParam,
}) => {
  const { t } = useTranslation();
  const [section, setSection] = useState<{
    value: string;
    label: string;
  } | null>(null);
  const [comment, setComment] = useState("");
  const [files, setFiles] = useState<File[]>([]);

  const options = [
    { value: "short_summary", label: t("feedback.shortSummary") },
    { value: "class", label: t("feedback.class") },
    { value: "chat", label: t("feedback.chat") },
    { value: "wrongInfo", label: t("feedback.wrongInfo") },
    { value: "other", label: t("feedback.other") },
  ];

  const { mutate } = useMutation({
    mutationFn: async () => {
      return await addFeedback(
        {
          category: section?.label,
          text: comment,
          code: regulatorCodeParam !== "" ? regulatorCodeParam : null,
          case_no: idParam !== "" ? idParam : null,
        },
        files,
      );
    },
    onSuccess: () => {
      toast.success(t("feedback.addFeedbackToaster"));
      setShowModal(false);
    },
  });

  const handleSubmit = () => {
    mutate();
  };

  const getSelectStyles = (customStyles = {}) => ({
    ...selectStyles(darkMode),
    input: (base: any) => ({
      ...base,
      color: darkMode ? "#FFFFFF" : "#000000",
    }),
    option: (base: any, { isFocused }: any) => ({
      ...base,
      backgroundColor: isFocused ? "#86BC25" : darkMode ? "#374151" : "#FFFFFF",
      color: darkMode ? "#FFFFFF" : "#000000",
    }),
    multiValue: (base: any) => ({
      ...base,
      backgroundColor: darkMode ? "#43B02A" : "#26890D",
    }),
    multiValueLabel: (base: any) => ({ ...base, color: "#e2e8f0" }),
    ...customStyles,
  });
  useEffect(() => {
    document.body.classList.add("overflow-hidden");

    return () => {
      document.body.classList.remove("overflow-hidden");
    };
  }, []);

  return (
    <div className="modal-overlay">
      <div className="modal-panel relative w-full max-w-lg px-6 py-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-xl title-base-color text-center flex-1">
            {t("feedback.feedbackTitle")}
          </h2>
          <button
            aria-label="Close"
            className="ml-2 -mt-3 -mr-3 modal-close-btn"
            onClick={() => setShowModal(false)}
            type="button"
          >
            <FaTimes className="text-xl text-gray-500 hover:text-del-red" />
          </button>
        </div>

        {/* Section select */}
        <div className="mb-4">
          <label className="block title-base-color mb-1">
            {t("feedback.section")} <span className="text-del-red">*</span>
          </label>
          <Select
            options={options}
            value={section}
            onChange={(option) => setSection(option)}
            classNamePrefix="react-select"
            placeholder={t("Select section")}
            styles={getSelectStyles()}
          />
        </div>

        {/* Comment textarea */}
        <div className="mb-4">
          <label className="block title-base-color mb-1">
            {t("feedback.comment")} <span className="text-del-red">*</span>
          </label>
          <textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            rows={4}
            className={`w-full px-3 py-2 rounded border focus:outline-none focus:ring-2 ${
              darkMode
                ? "bg-gray-700 text-white border-gray-600 focus:ring focus:ring-green-500"
                : "bg-gray-100 text-black border-gray-300 focus:ring focus:ring-green-400"
            }`}
          />
        </div>

        {/* FileDropzone */}
        <div className="mb-4">
          <label className="block title-base-color mb-1">
            {t("feedback.files")}
          </label>
          <FileDropzone setFiles={setFiles} />
        </div>

        {/* Buttons */}
        <div className="flex justify-end gap-2">
          <button
            onClick={() => setShowModal(false)}
            className="px-4 py-2 rounded bg-gray-400 text-white hover:bg-gray-500"
          >
            {t("feedback.cancel")}
          </button>
          <button
            onClick={handleSubmit}
            className={`px-4 py-2 ${
              comment !== "" && !!section ? "btn-green" : "btn-disabled"
            }`}
          >
            {t("feedback.send")}
          </button>
        </div>
      </div>
    </div>
  );
};

export { FeedbackModal };
